import type { FormVersion } from "../state/formVersion";
import { useFormVersionState } from "../state/formVersion";
import type { Form } from "../state/form";
import { useFormState } from "../state/form";
import { initialized } from "../utils/initializedUtil";
import { createEffect, createSignal, on, untrack } from "solid-js";
import { isNil } from "lodash-es";
import ApiService from "./ApiService";
import { makeEventListener } from "@solid-primitives/event-listener";
import { trackStore } from "@solid-primitives/deep";
import logger from "../utils/logger";
import type CustomerId from "../state/CustomerId";
import { makeWindowEventListener } from "../utils/makeWindowEventListener";
import { literal } from "valibot";
import {
  PREVIEW_MESSAGE_DATA_SHOW,
  PREVIEW_URL,
  sendClosedPayload,
  sendMappedFormDataPayload,
  sendTokenPayload,
} from "../utils/previewUtil.ts";

const TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000; // Five minutes
let formVersionStore: FormVersion | null = null;
let formStore: Form | null = null;
let customerId: CustomerId | null = null;
let previewWindow!: { get: () => Window | null; set: (value: Window) => void };

const [intervalId, setIntervalId] = createSignal<NodeJS.Timeout>();

const init = (cusId: CustomerId) => {
  formVersionStore = useFormVersionState();
  formStore = useFormState();
  customerId = cusId;
  previewWindow = (() => {
    let window: Window | null = null;

    return {
      get: () => {
        if (isNil(window)) {
          return null;
        }

        if (window.closed) {
          window = null;
          return null;
        }

        return window;
      },
      set: (value: Window) => (window = value),
    };
  })();

  createEffect(
    on(
      () => [
        trackStore(getFormVersionStore().rules),
        trackStore(getFormVersionStore().fields),
        trackStore(getFormVersionStore().variables),
        trackStore(getFormVersionStore().theme),
        trackStore(getFormVersionStore().subforms),
        trackStore(getFormStore().meta),
      ],
      () => {
        updatePreview();
      },
      { defer: true },
    ),
  );
};

const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");
const getFormStore = () => initialized(formStore, "formStore");

const updateToken = async (window: Window | null) => {
  if (isNil(window)) {
    clearInterval(intervalId());
    setIntervalId(undefined);
    return;
  }
  getToken().then((token) => sendTokenPayload(window, token));
};

const updatePreview = () => {
  const window = previewWindow.get();
  if (isNil(window) || isNil(customerId)) {
    return;
  }
  sendMappedFormDataPayload(window, getFormStore(), getFormVersionStore(), customerId);
};

makeWindowEventListener(
  () => previewWindow?.get(),
  literal(PREVIEW_MESSAGE_DATA_SHOW),
  () => {
    untrack(() => {
      updatePreview();
      const window = previewWindow.get();

      // Periodically send new tokens for API access (images/dataSources)
      if (isNil(intervalId())) {
        setIntervalId(setInterval(() => updateToken(window), TOKEN_REFRESH_INTERVAL));
      }
      if (!isNil(window)) {
        // And send one right away
        getToken().then((token) => sendTokenPayload(window, token));
      }
    });
  },
);

makeEventListener(window, "beforeunload", () => {
  if (isOpen()) {
    sendClosedPayload(previewWindow.get());
  }
});

const getToken = async () => ApiService.getPreviewToken(getFormStore().id);

const createNewPreviewWindow = () => {
  const windowWidth = 460;
  const windowHeight = 700;
  const margin = 20;
  const windowTopBar = 30; // Approximation, different per browser

  const screenWidth = window.screen.availWidth;
  const screenHeight = window.screen.availHeight;

  const left = screenWidth - windowWidth - margin;
  const top = screenHeight - windowHeight - margin - windowTopBar;

  // On some display devices the window position is off (due to wrong screen sizes), but always in the bottom right corner
  const windowFeatures = `popup=true, width=${windowWidth}, height=${windowHeight}, left=${left}, top=${top}`;

  const newWindow = window.open(PREVIEW_URL, "moreapp-preview", windowFeatures);

  if (!isNil(newWindow)) {
    previewWindow.set(newWindow);
  } else {
    logger.error("Failed to open window");
  }
};

const focusExistingPreviewWindow = () => previewWindow.get()?.focus();
const isOpen = () => !isNil(previewWindow?.get());

const PreviewService = {
  init,
  openPreview: () => (isOpen() ? focusExistingPreviewWindow() : createNewPreviewWindow()),
};

export default PreviewService;
